import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { UsuarioSignIn } from 'src/app/models/User/UsuarioSignIn.model';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';

/**
 * Formulário dos campos de credencial, usado pelo login e pelo modal "Aprovar acesso".
 * Controles: user (usuário), password (senha) e authMode ('admin' ou 'user').
 */
export function criarFormularioDeCredenciais(formBuilder: FormBuilder): FormGroup {
  return formBuilder.group({
    user: [''],
    password: ['', [Validators.required, Validators.maxLength(30), Validators.minLength(8)]],
    authMode: ['admin']
  });
}

/**
 * Monta as credenciais a enviar, igual para o login e para o modal.
 * asAdministrator é verdadeiro com um único usuário ou com a opção "Entrar como Administrador";
 * o usuário só é enviado na opção "Entrar como usuário".
 */
export function montarCredenciais(form: FormGroup, mostrarOpcoes: boolean): Pick<UsuarioSignIn, 'password' | 'user' | 'asAdministrator'> {
  const comoAdministrador = !mostrarOpcoes || form.controls['authMode'].value === 'admin';

  return {
    password: form.controls['password'].value,
    user: comoAdministrador ? undefined : (form.controls['user'].value || '').trim().toLowerCase() || undefined,
    asAdministrator: comoAdministrador
  };
}

/**
 * Campos de credencial reutilizáveis (login e modal "Aprovar acesso").
 * - mostrarOpcoes = false: só o campo de senha (conta com um único usuário).
 * - mostrarOpcoes = true: rádios "Entrar como Administrador" (senha) e "Entrar como usuário" (usuário e senha).
 * O foco, a limpeza dos campos ao trocar de opção e as proteções da senha ficam aqui.
 */
@Component({
  selector: 'app-auth-credentials-fields',
  templateUrl: './auth-credentials-fields.component.html',
  styleUrls: ['./auth-credentials-fields.component.scss']
})
export class AuthCredentialsFieldsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() email: string = '';
  @Input() mostrarOpcoes: boolean = false;

  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;
  @ViewChild('userNameInput') userNameInput?: ElementRef<HTMLInputElement>;

  hide: boolean = true;
  private destroy$ = new Subject<void>();

  constructor(private messageService: InvalidFieldMessageService) { }

  get message() {
    return this.messageService;
  }

  get campos() {
    return this.form.controls;
  }

  get pedeUsuario(): boolean {
    return this.mostrarOpcoes && this.campos['authMode'].value === 'user';
  }

  ngOnInit(): void {
    // Trocar entre administrador e usuário limpa os campos e leva o foco para o campo certo
    this.form.get('authMode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.limparCampos();
        this.focarCampoInicial();
      });
  }

  ngAfterViewInit(): void {
    this.focarCampoInicial();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private limparCampos(): void {
    ['user', 'password'].forEach(nome => {
      const campo = this.campos[nome];
      campo.reset();
      campo.markAsUntouched();
      campo.markAsPristine();
    });
  }

  // Usuário no modo "usuário"; senha nos demais casos. setTimeout: espera a tela ser desenhada
  private focarCampoInicial(): void {
    setTimeout(() => {
      const campo = this.pedeUsuario ? this.userNameInput : this.passwordInput;
      campo?.nativeElement.focus();
    });
  }
}
